import type {
  MiceBudgetView,
  MiceCorporatePath,
  MiceNamedRef,
  MiceProjectView,
} from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button, IconButton, ToneChip } from '@web/ui/core';
import { Drawer } from '@web/ui/core/organisms';
import { ActivityFeed } from './activity_feed';
import { BudgetSection } from './budget_section';
import { ExecutionSummary, executionStats } from './execution_summary';
import { premisesPhrases } from './premises_summary';

interface EventSummaryDrawerProps {
  open: boolean;
  onClose: () => void;
  project: MiceProjectView;
  /** Abre o painel de edição das premissas. */
  onEditPremises: () => void;
  /** Leva a lista para a fase Atenção. */
  onShowAttention: () => void;
  /** O orçamento gerado, para quem tem `mice:purchase`; sem ele, a seção não aparece. */
  budget: MiceBudgetView | undefined;
  onDownloadBudget: () => void;
}

const EYEBROW =
  'font-mono text-tiny font-medium uppercase tracking-eyebrow-wide text-faint-foreground';

function Section({
  title,
  action,
  children,
}: Readonly<{
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}>): React.JSX.Element {
  return (
    <section aria-label={title} className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className={EYEBROW}>{title}</span>
        {action}
      </div>
      {children}
    </section>
  );
}

/** O nome no caminho `event`; nos outros, de onde o valor vem. */
function corporateValue(
  ref: MiceNamedRef | null,
  path: MiceCorporatePath,
  field: 'costCenter' | 'motive',
): string {
  if (ref) return ref.name;
  return path === 'event'
    ? t('mice.corporatePath.undefined')
    : t(`mice.corporatePath.${field}.${path}`);
}

function corporateRows(project: MiceProjectView): { label: string; value: string }[] {
  return [
    { label: t('mice.summary.company'), value: project.company.name },
    { label: t('mice.form.debitCompany'), value: project.debitCompany.name },
    {
      label: t('mice.form.costCenter'),
      value: corporateValue(project.costCenter, project.costCenterPath, 'costCenter'),
    },
    {
      label: t('mice.form.motive'),
      value: corporateValue(project.motive, project.motivePath, 'motive'),
    },
    ...(project.corporateProject
      ? [{ label: t('mice.form.corporateProject'), value: project.corporateProject.name }]
      : []),
  ];
}

/** O que sai do topo da página: o orçamento, a compra, as premissas, os dados corporativos e a atividade. */
export function EventSummaryDrawer(props: Readonly<EventSummaryDrawerProps>) {
  const { project } = props;
  const checked = project.premisesCheckedAt !== null;
  return (
    <Drawer open={props.open} onClose={props.onClose} ariaLabel={t('mice.summary.title')}>
      <div className="flex flex-shrink-0 items-center justify-between border-b border-border-soft px-5 py-4">
        <span className="font-serif text-drawer-title text-foreground">
          {t('mice.summary.title')}
        </span>
        <IconButton
          icon="close"
          variant="outline"
          onClick={props.onClose}
          title={t('mice.drawer.close')}
          aria-label={t('mice.drawer.close')}
        />
      </div>
      <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-5 py-5">
        {props.budget ? (
          <Section title={t('mice.budget.title')}>
            <BudgetSection budget={props.budget} onDownload={props.onDownloadBudget} />
          </Section>
        ) : null}
        {executionStats(project.counts).total > 0 ? (
          <Section title={t('mice.summary.purchase')}>
            <ExecutionSummary
              counts={project.counts}
              needsYou={project.phaseCounts.attention}
              onShowAttention={props.onShowAttention}
            />
          </Section>
        ) : null}
        <Section
          title={t('mice.summary.premises')}
          action={
            <Button size="sm" variant="ghost" onClick={props.onEditPremises}>
              {t('mice.summary.premisesEdit')}
            </Button>
          }
        >
          <div>
            <ToneChip tone={checked ? 'green' : 'amber'}>
              {t(checked ? 'mice.premises.checked' : 'mice.premises.unchecked')}
            </ToneChip>
          </div>
          <div className="flex flex-col gap-1 text-meta text-foreground-default">
            {(project.premises ? premisesPhrases(project.premises) : []).map((phrase) => (
              <span key={phrase}>{phrase}</span>
            ))}
          </div>
        </Section>
        <Section title={t('mice.form.corporateSection')}>
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-meta">
            {corporateRows(project).map((row) => (
              <div key={row.label} className="contents">
                <dt className="text-muted-foreground">{row.label}</dt>
                <dd className="text-foreground">{row.value}</dd>
              </div>
            ))}
          </dl>
        </Section>
        <ActivityFeed projectId={project.id} />
      </div>
    </Drawer>
  );
}
